import { useQuery } from '@tanstack/react-query';

import customComponentsColorMap from '../../constants/customComponentColors';

import { BaseError } from '../../helpers/BaseError';

import {
    ComponentType,
    ComponentTypeTransformed,
} from '../../models/component-types-model';
import { AllVariantType } from '../../models/variant.model';

import API from '../../services/api';

import componentTypeQueryKeys from './_queryKeys';

const getComponentTypes = async (): Promise<ComponentType[]> => {
    const { data } = await API.getComponentTypes();
    return data;
};

export default function useComponentTypesQuery() {
    return useQuery<ComponentType[], BaseError, ComponentTypeTransformed>({
        queryKey: componentTypeQueryKeys.components,
        queryFn: () => getComponentTypes(),
        staleTime: 24 * 60 * 60 * 1000,
        select: (data) => {
            const mappedData = data.map((ct) => ({
                ...ct,
                ...customComponentsColorMap[ct.base_color],
            }));
            return {
                list: mappedData,
                map: Object.fromEntries(
                    mappedData.map((ct) => [ct.data_key, ct]),
                ),
                mapById: Object.fromEntries(
                    mappedData.map((ct) => [ct.id, ct]),
                ),
                selectOptions: mappedData
                    .filter((ct) => !ct.archived_at)
                    .map((ct) => ({
                        label: ct.name,
                        value: ct.data_key,
                        componentTypeId: ct.id,
                    })),
                selectOptionsWithAll: [
                    { label: 'All', value: AllVariantType.All },
                    ...mappedData
                        .filter((ct) => !ct.archived_at)
                        .map((ct) => ({
                            label: ct.name,
                            value: ct.data_key,
                        })),
                ],
                allComponentTypes: data
                    .filter((ct) => !ct.archived_at)
                    .map((ct) => ct.data_key),
            };
        },
    });
}
